/**
 * Productos de crédito: tipos de garantía, destinos del crédito y reglas que
 * validan las condiciones solicitadas contra el producto.
 */
import { keyedEnum } from '../catalogs/enum.js';
import { formatMoney, formatRate } from '../money/format.js';
import { PAYMENT_FREQUENCY_LABELS } from './frequency.js';
import { REPAYMENT_METHOD_LABELS, repaymentTermsViolations } from './repayment.js';
/** Plazo máximo de un préstamo, en cuotas (30 años de cuotas mensuales). */
export const MAX_TERM_PERIODS = 360;
/** Máximo de períodos de gracia a capital que admite un producto. */
export const MAX_PRINCIPAL_GRACE_PERIODS = 60;
/** Días de gracia antes de cobrar mora que se proponen al crear un producto. */
export const DEFAULT_LATE_GRACE_DAYS = 3;
/** Máximo de días de gracia de mora configurables. */
export const MAX_LATE_GRACE_DAYS = 60;
/** Cobertura mínima de garantía propuesta: 125 % del capital (en puntos básicos). */
export const DEFAULT_MIN_COLLATERAL_COVERAGE_BP = 12_500;
/** Préstamos activos simultáneos por cliente que se proponen por defecto. */
export const DEFAULT_MAX_ACTIVE_LOANS_PER_CLIENT = 1;
/** Tope configurable de préstamos activos simultáneos por cliente. */
export const MAX_ACTIVE_LOANS_PER_CLIENT_LIMIT = 10;
/** Cómo se garantiza un préstamo. Determina qué etapas de originación aplican. */
export const GUARANTEE_TYPES = ['fiduciary', 'pledge', 'mortgage', 'solidarity_group', 'mixed'];
/** Catálogo con clave de tipos de garantía: `GUARANTEE_TYPE.mortgage`. */
export const GUARANTEE_TYPE = keyedEnum(GUARANTEE_TYPES);
/** Etiquetas en español de cada tipo de garantía. */
export const GUARANTEE_TYPE_LABELS = {
    fiduciary: 'Fiduciaria (fiador)',
    pledge: 'Prendaria',
    mortgage: 'Hipotecaria',
    solidarity_group: 'Grupo solidario',
    mixed: 'Mixta',
};
/** Tipos de garantía que exigen un bien físico avaluado. */
export const COLLATERAL_BACKED_GUARANTEES = ['pledge', 'mortgage', 'mixed'];
/** Para qué es el dinero (destino del crédito); se reporta en la analítica de cartera. */
export const LOAN_PURPOSES = [
    'working_capital',
    'fixed_assets',
    'agriculture',
    'livestock',
    'commerce',
    'services',
    'manufacturing',
    'housing_improvement',
    'debt_consolidation',
    'other',
];
/** Etiquetas en español de cada destino. */
export const LOAN_PURPOSE_LABELS = {
    working_capital: 'Capital de trabajo',
    fixed_assets: 'Activo fijo / maquinaria',
    agriculture: 'Agrícola',
    livestock: 'Pecuario',
    commerce: 'Comercio',
    services: 'Servicios',
    manufacturing: 'Producción / artesanía',
    housing_improvement: 'Mejora de vivienda productiva',
    debt_consolidation: 'Consolidación de deudas',
    other: 'Otro',
};
/** Motivos legibles por los que las condiciones quedan fuera del producto. Vacío = válido. */
export function productViolations(product, terms) {
    const violations = [];
    if (terms.principal < product.minAmount || terms.principal > product.maxAmount) {
        violations.push(`El monto debe estar entre ${formatMoney(product.minAmount)} y ${formatMoney(product.maxAmount)}`);
    }
    if (terms.termPeriods < product.minTermPeriods || terms.termPeriods > product.maxTermPeriods) {
        violations.push(`El plazo debe estar entre ${String(product.minTermPeriods)} y ${String(product.maxTermPeriods)} cuotas`);
    }
    if (terms.annualInterestRate < product.minAnnualRate || terms.annualInterestRate > product.maxAnnualRate) {
        violations.push(`La tasa debe estar entre ${formatRate(product.minAnnualRate)} y ${formatRate(product.maxAnnualRate)} anual`);
    }
    if (!product.frequencies.includes(terms.frequency)) {
        violations.push(`El producto no permite pagos ${PAYMENT_FREQUENCY_LABELS[terms.frequency].toLowerCase()}es`);
    }
    if (terms.principalGracePeriods > product.maxPrincipalGracePeriods) {
        violations.push(`El producto permite máximo ${String(product.maxPrincipalGracePeriods)} períodos de gracia`);
    }
    if (terms.principalGracePeriods >= terms.termPeriods) {
        violations.push('La gracia a capital debe ser menor que el plazo');
    }
    if (!product.repaymentMethods.includes(terms.repaymentMethod)) {
        violations.push(`El producto no ofrece la forma de pago "${REPAYMENT_METHOD_LABELS[terms.repaymentMethod]}"`);
    }
    violations.push(...repaymentTermsViolations(terms));
    if (!product.guaranteeTypes.includes(terms.guaranteeType)) {
        violations.push('El tipo de garantía no está permitido para este producto');
    }
    if (!product.purposes.includes(terms.purpose)) {
        violations.push(`El producto no financia el destino "${LOAN_PURPOSE_LABELS[terms.purpose]}"`);
    }
    return violations;
}
/** Indica si el tipo de garantía exige avalúo de un bien. */
export function requiresCollateral(guaranteeType) {
    return COLLATERAL_BACKED_GUARANTEES.includes(guaranteeType);
}
//# sourceMappingURL=product.js.map